import React, { useState } from 'react';
import { api } from '../services/api';

interface StockData {
  symbol: string;
  price: string;
  change: string;
  percent: string;
  currency: string;
  sources?: { title: string; uri: string }[];
}

interface StockTickerProps {
  initialTicker?: string;
}

const StockTicker: React.FC<StockTickerProps> = ({ initialTicker = '' }) => {
  const [ticker, setTicker] = useState(initialTicker);
  const [stockData, setStockData] = useState<StockData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFetch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!ticker.trim()) return;

    setIsLoading(true);
    setError(null);
    setStockData(null);

    try {
      // Use backend API to get quote data
      const response = await api.getQuote(ticker);
      if (response.error) {
        throw new Error(response.error);
      }
      setStockData(response.data as StockData);
    } catch (err) {
      setError('Could not fetch market data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          Live Market Data
        </h3>
      </div>

      <form onSubmit={handleFetch} className="flex gap-2">
        <input
          type="text"
          value={ticker}
          onChange={(e) => setTicker(e.target.value.toUpperCase())}
          placeholder="Ticker (e.g. AAPL)"
          className="flex-1 bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
        />
        <button
          type="submit"
          disabled={isLoading || !ticker}
          className="bg-slate-800 hover:bg-slate-700 text-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed p-2 rounded-lg border border-slate-700 transition-colors"
        >
          {isLoading ? (
            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
        </button>
      </form>

      {error && (
        <div className="text-xs text-rose-400 bg-rose-900/20 p-2 rounded border border-rose-900/50">
          {error}
        </div>
      )}

      {stockData && (
        <div className="animate-fade-in">
          <div className="flex items-baseline justify-between">
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-white tracking-tight">{stockData.price}</span>
              <span className="text-xs text-slate-500 font-mono">{stockData.currency}</span>
            </div>
            <div className={`flex items-center text-sm font-medium px-2 py-1 rounded ${
              stockData.change.includes('-') 
                ? 'text-rose-400 bg-rose-500/10' 
                : 'text-emerald-400 bg-emerald-500/10'
            }`}>
              {stockData.change.includes('-') ? '↓' : '↑'} {stockData.change} ({stockData.percent})
            </div>
          </div>
          
          {/* Attribution / Sources */}
          {stockData.sources && stockData.sources.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-800">
              <p className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">Sources</p>
              <div className="flex flex-wrap gap-2">
                {stockData.sources.map((source, idx) => (
                  <a 
                    key={idx}
                    href={source.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-blue-400 hover:text-blue-300 hover:underline truncate max-w-[150px]"
                  >
                    {source.title}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StockTicker;
